import { Button } from "../../../../components/Button";
import { FilterButton } from "./FilterButton";
import { ColumnSettingsButton } from "./ColumnSettingsButton";
import type { DefaultTagDefinition } from "../../../../types/tags";
import type { PlayersColumnLayout } from "./playersTableColumns";
import { tourAnchor } from "../../../tours/tourAnchors";
import "./PlayersToolbar.css";
import { SearchIcon } from "../../../../assets/SearchIcon";
import { ImportIcon } from "../../../../assets/ImportIcon";
import { ExportIcon } from "../../../../assets/ExportIcon";
import { AddIcon } from "../../../../assets/AddIcon";

interface PlayersToolbarProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  onAddProspect: () => void;
  onAddTeam: () => void;
  /** Opens the bulk manage-teams panel (delete several teams, restore deleted ones). */
  onManageTeams?: () => void;
  /**
   * Opens the exports panel, which is where an export is both started and collected.
   * One button rather than two: "Export" and "Exports" side by side differed by a letter
   * and gave no clue that one queued work and the other listed it.
   */
  onExport: () => void;
  onUploadRoster: () => void;
  onFilterClick: () => void;
  activeFilterCount: number;
  /**
   * Column picker data. Optional together — a toolbar without them simply omits the control, so a
   * caller that has no table to configure is not forced to invent a layout.
   */
  tagDefinitions?: DefaultTagDefinition[];
  columnLayout?: PlayersColumnLayout;
  onColumnLayoutChange?: (layout: PlayersColumnLayout) => void;
  selectedCount?: number;
  onDeleteSelected?: () => void;
  onAddToTeam?: () => void;
  /**
   * Drops the selection and returns the toolbar to normal. Optional: without it the bar simply
   * offers no way out but unticking rows, which is worse but not broken.
   */
  onClearSelection?: () => void;
  /** Whether the current user may merge players (any authenticated user). */
  canMerge?: boolean;
  onMerge?: () => void;
  // Reorder mode
  isReorderMode?: boolean;
  canReorder?: boolean;
  isLoadingReorder?: boolean;
  isSavingOrder?: boolean;
  onEnterReorderMode?: () => void;
  onSaveOrder?: () => void;
  onCancelReorder?: () => void;
}

export const PlayersToolbar = ({
  searchQuery,
  onSearchChange,
  onAddProspect,
  onAddTeam,
  onManageTeams,
  onExport,
  onUploadRoster,
  onFilterClick,
  activeFilterCount,
  tagDefinitions,
  columnLayout,
  onColumnLayoutChange,
  selectedCount = 0,
  onDeleteSelected,
  onAddToTeam,
  onClearSelection,
  canMerge = false,
  onMerge,
  isReorderMode = false,
  canReorder = false,
  isLoadingReorder = false,
  isSavingOrder = false,
  onEnterReorderMode,
  onSaveOrder,
  onCancelReorder,
}: PlayersToolbarProps) => {
  if (isReorderMode) {
    return (
      <div className="players-toolbar players-toolbar--reorder">
        <span className="players-toolbar__reorder-hint">
          Drag rows to reorder prospects
        </span>
        <div className="players-toolbar__actions">
          <Button
            variant="secondary"
            onClick={onCancelReorder}
            disabled={isSavingOrder}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={onSaveOrder}
            disabled={isSavingOrder}
          >
            {isSavingOrder ? "Saving..." : "Save Order"}
          </Button>
        </div>
      </div>
    );
  }

  /*
    A selection is its own mode, and it replaces the toolbar rather than adding to it.

    Appending the selection actions to the normal row put eleven buttons on one line, which squeezed
    the search field and left the whole thing cramped. It was also the wrong information: with
    players selected, Export, Upload Roster, Manage Teams, Add Team and Add Player are all about
    something other than the players in hand.

    Reorder mode above already works this way, and search is a natural casualty in both — the
    section clears the selection whenever the query changes, so a search box over a live selection
    was never going to survive being used.
  */
  if (selectedCount > 0) {
    const noun = selectedCount === 1 ? "player" : "players";

    return (
      <div className="players-toolbar players-toolbar--selection">
        <div className="players-toolbar__selection-summary">
          <span className="players-toolbar__selection-count">
            {selectedCount} {noun} selected
          </span>
          {onClearSelection && (
            <button
              type="button"
              className="players-toolbar__selection-clear"
              onClick={onClearSelection}
              data-testid="clear-selection-button"
            >
              Clear
            </button>
          )}
        </div>
        <div className="players-toolbar__actions">
          <Button variant="secondary" onClick={onAddToTeam}>
            Add to Team
          </Button>
          {/*
            Two is the minimum a merge can act on, so it appears at two rather than showing up
            disabled at one. No disabled state and no title: it is only rendered when it works,
            which leaves nothing for either to say.
          */}
          {canMerge && selectedCount >= 2 && (
            <Button
              variant="secondary"
              className="players-toolbar__merge-btn"
              onClick={onMerge}
              data-testid="merge-players-button"
            >
              Merge ({selectedCount})
            </Button>
          )}
          <Button
            variant="secondary"
            className="players-toolbar__delete-btn"
            onClick={onDeleteSelected}
          >
            Delete ({selectedCount})
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="players-toolbar">
      <div className="players-toolbar__search">
        <SearchIcon className="players-toolbar__search-icon" />
        <input
          type="text"
          className="players-toolbar__search-input"
          placeholder="Search player"
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          {...tourAnchor("players.search")}
        />
      </div>
      <div className="players-toolbar__actions">
        {tagDefinitions && columnLayout && onColumnLayoutChange && (
          <ColumnSettingsButton
            tagDefinitions={tagDefinitions}
            layout={columnLayout}
            onLayoutChange={onColumnLayoutChange}
          />
        )}
        <FilterButton
          activeFilterCount={activeFilterCount}
          onClick={onFilterClick}
          anchor="players.filter"
        />
        {canReorder && (
          <Button
            variant="secondary"
            onClick={onEnterReorderMode}
            disabled={isLoadingReorder}
            title="Drag and drop to reorder prospects"
          >
            {isLoadingReorder ? "Loading..." : "Reorder"}
          </Button>
        )}
        <Button variant="secondary" onClick={onExport} data-testid="export-button">
          <ExportIcon />
          Export
        </Button>
        <Button
          variant="secondary"
          onClick={onUploadRoster}
          {...tourAnchor("players.upload")}
        >
          <ImportIcon />
          Upload Roster
        </Button>
        {onManageTeams && (
          <Button
            variant="secondary"
            onClick={onManageTeams}
            title="Archive teams you no longer recruit, or bring archived ones back"
            data-testid="manage-teams-button"
            {...tourAnchor("players.teams.manage")}
          >
            Archive Teams
          </Button>
        )}
        <Button variant="secondary" onClick={onAddTeam}>
          <AddIcon />
          Add Team
        </Button>
        <Button
          variant="primary"
          onClick={onAddProspect}
          {...tourAnchor("players.add")}
        >
          <AddIcon />
          Add Player
        </Button>
      </div>
    </div>
  );
};
